import { Subject } from 'rxjs';

import { getApiUrl, API_URL } from '@/utils/api';
import { getMainLocale } from '@/utils/locales';
import { AppFullLocale } from '@/types';

import ApiBase from './ApiBase';

// eslint-disable-next-line no-var
declare var HiPay: any;

export default class HipayExproService extends ApiBase {
  static baseUrl = getApiUrl(API_URL.ADMIN, '/payment');

  hipayInstance = undefined;
  cardInstance = undefined;

  constructor(api) {
    super(api);
  }

  /**
   * Initialize the HiPay core
   *
   * @param {*} locale The locale of the current site
   * @param {*} username The public username to instantiate hipay form
   * @param {*} password The public password to instantiate hipay form
   */
  initHipay(locale: AppFullLocale, username: string, password: string) {
    if (this.hipayInstance === undefined) {
      const HIPAY_ENVIRONMENT = process.env.NEXT_PUBLIC_HIPAY_ENVIRONMENT || 'stage';
      const lang = getMainLocale(locale);
      // eslint-disable-next-line no-undef, new-cap
      this.hipayInstance = HiPay({
        username: username,
        password: password,
        environment: HIPAY_ENVIRONMENT,
        lang
      });
    }
  }

  /**
   * Create a hipay form instance on the given HTML tag id
   *
   * @param {*} options Options for Hipay form instanciation
   * @return {Observable} An observable object
   */
  createHipayForm(options) {
    const event = new Subject();

    if (this.cardInstance) {
      this.cardInstance.destroy();
    }

    try {
      this.cardInstance = this.hipayInstance.create('card', this.optionsToHiPay(options));

      this.cardInstance.on('change', (response) => {
        if (response.valid) {
          this.cardInstance.getPaymentData().then((paymentData) => {
            event.next(paymentData);
          });
        } else {
          event.next(undefined);
        }
      });
    } catch (error) {
      event.error(error);
    }

    return event;
  }

  /**
   * Update a credit card token with cvc
   *
   * @param {*} params Parameters for update the token
   * @return {Observable} An observable object
   */
  updateSaveCreditCardCvc(params) {
    const updateCvcSubject = new Subject();

    try {
      if (this.hipayInstance) {
        this.hipayInstance.updateToken(params).then(
          (response) => {
            updateCvcSubject.next(response);
          },
          (error) => {
            updateCvcSubject.error(error);
          }
        );
      }
    } catch (error) {
      updateCvcSubject.error(error);
    }

    return updateCvcSubject;
  }

  /**
   * Map internal options to Hipay ones
   *
   * @param {*} options The internal form options
   * @return {*} The form info converted Hipay cart format
   */
  optionsToHiPay(options) {
    return {
      fields: {
        cardHolder: {
          selector: options.cardHolder.selector,
          placeholder: options.cardHolder.placeholder
        },
        cardNumber: {
          selector: options.cardNumber.selector,
          placeholder: options.cardNumber.placeholder
        },
        expiryDate: {
          selector: options.expiryDate.selector
        },
        cvc: {
          selector: options.cvc,
          helpSelector: `${options.cvc}-help`
        }
      },
      styles: {
        base: {
          fontSize: '16px'
        }
      }
    };
  }

  /**
   * Map Hipay card fields to Millesima ones
   *
   * @param {*} creditCardInfo Hipay credit card info
   * @return {*} The card info converted to Millesima cart format
   */
  mapHipayCreditCardInfo(creditCardInfo) {
    return creditCardInfo ? {
      brand: creditCardInfo.brand,
      paymentProduct: creditCardInfo.payment_product,
      cardExpireMonth: creditCardInfo.card_expiry_month,
      cardExpireYear: creditCardInfo.card_expiry_year,
      cardHolder: creditCardInfo.card_holder,
      cardType: creditCardInfo.card_type,
      country: creditCardInfo.country,
      forbidden_issuer_country: creditCardInfo.forbidden_issuer_country,
      pan: creditCardInfo.pan,
      token: creditCardInfo.token
    } : undefined;
  }

  getBrowserInfo() {
    console.log(this.hipayInstance.getBrowserInfo());
    return this.hipayInstance.getBrowserInfo();
  }

  getFingerprint() {
    return this.hipayInstance.getDeviceFingerprint();
  }

  /**
   * get Hipay credentials
   *
   * @param {String} storeId The actual store id
   * @return {Object} {
   hipayLogin: String,
   hipayPassword: String
   }
   */
  getHipayCredential(storeId: string) {
    return this.api.getJSON(`${HipayExproService.baseUrl}/publicIdentifierHiPay?storeId=${storeId}`);
  }
}
